import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import useCheckbox from '../hooks/useCheckbox';
import TableBody from '../TableBody/TableBody';
import TableHeader from '../TableHeader/TableHeader';
import BulkOperations from '../TableRow/BulkOperations';
import TotalSummary from '../TableRow/TotalSummary';
import { ITableSettingsData, ITripsOverviewColumn, ITripsOverviewTableData } from '../interfaces';
import {
  columnsAtom,
  rideBookDataAtom,
  rideItemIdsAtom,
  showSummariesAtom,
  tableSettingsStateAtom,
} from '../TripsOverviewState';

interface ITableProps {
  updateTable: () => void;
  handleAction: (action: string) => void;
  selectAllSectionCheckBox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  allCheckedRowsOpened: boolean | null;
  allCheckedRowsClosed: boolean | null;
}

function Table(props: ITableProps): JSX.Element {
  const tableData = useRecoilValue<ITripsOverviewTableData | null>(rideBookDataAtom);
  const { checkedRecords, checkOneCheckbox, selectSectionChecbox } = useCheckbox();
  const tableSettingsState = useRecoilValue<ITableSettingsData | null>(tableSettingsStateAtom);
  const showSummaries = useRecoilValue(showSummariesAtom);
  const { formatMessage: f } = useIntl();
  const rideItemIds = useRecoilValue<string[]>(rideItemIdsAtom);
  const columns = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);

  React.useEffect(() => {
    // slider
    const slider: any = document.querySelector('.js-table-drag');
    if (slider) {
      let isDown = false;
      let startX: number;
      let scrollLeft: number;
      slider.addEventListener(
        'mousedown',
        (e: any) => {
          isDown = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        },
        { passive: true }
      );
      slider.addEventListener(
        'mouseleave',
        () => {
          isDown = false;
          slider.classList.remove('user-select-off');
        },
        { passive: true }
      );
      slider.addEventListener(
        'mouseup',
        () => {
          isDown = false;
          slider.classList.remove('user-select-off');
        },
        { passive: true }
      );
      slider.addEventListener(
        'mousemove',
        (e: any) => {
          if (!isDown || document.getElementById('rides-info')) {
            return;
          }
          slider.classList.add('user-select-off');
          const x = e.pageX - slider.offsetLeft;
          const walk = (x - startX) * 3; // scroll-fast
          slider.scrollLeft = scrollLeft - walk;
        },
        { passive: true }
      );
      const tableFilter: any = document.getElementsByClassName('table-filter');
      const disableEvents = (event: MouseEvent): boolean => {
        event.stopPropagation();
        return false;
      };
      if (tableFilter) {
        // eslint-disable-next-line no-restricted-syntax
        for (const item of tableFilter) {
          item.addEventListener('mousemove', disableEvents);
        }
      }
    }

    // IntersectionObserver
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle('is-sticky', e.intersectionRatio < 1),
      { threshold: [1] }
    );
    const selection = document.querySelector('.w-selection-summary');
    if (selection) {
      observer.observe(selection);
    }
  });

  const filterHiddenColumns = columns.filter((item) => !item.hidden);

  return (
    <>
      <div className="w-selection-summary">
        <BulkOperations handleAction={props.handleAction} />
        {tableData &&
          tableData.summaries &&
          tableSettingsState?.overviewSummary &&
          showSummaries && (
            <TotalSummary
              summariesData={tableData.summaries.find((item) => item.type === 'overview')}
            />
          )}
      </div>

      <div
        id="table-overview"
        className="w-table-prehlad-jazd js-table-drag table-drag"
        data-cy="ride-book-table"
      >
        <div className="w-selection-summary-actions-wrapper">
          <div id="table-overview-header-actions" className="w-selection-summary-actions th-sticky">
            {f({ id: 'table.actionsBtn' })}
          </div>
        </div>

        <div className="b-table-prehlad-jazd">
          <TableHeader
            selectAllRows={props.selectAllSectionCheckBox}
            columns={filterHiddenColumns}
          />

          {rideItemIds.length === 0 && (
            <div
              className="p-6 d-flex align-items-center d-flex align-items-center text-center"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '360px',
                fontSize: '24px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <div data-cy="table-ridebook-no-data" style={{ color: '#B3B3B3' }}>
                {f({ id: 'table.noData.title' })}
                <br />
                {f({ id: 'table.noData.text' })}
              </div>
            </div>
          )}
          {tableData && rideItemIds.length > 0 && (
            <TableBody
              tableData={tableData}
              selectOneRow={checkOneCheckbox}
              selectedRecords={checkedRecords}
              selectSectionCheckbox={selectSectionChecbox}
              updateTable={props.updateTable}
              allCheckedRowsOpened={props.allCheckedRowsOpened}
              allCheckedRowsClosed={props.allCheckedRowsClosed}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(Table);
